import { matchesSelector } from '../../dist/js/functions.min.js';

document.addEventListener('DOMContentLoaded', () => {
	//
	const rootSelector = '.elementary-partner-finder-2025';
	const resultsSelector = '.elementary-partner-finder-2025__results';
	const noResultsSelector = '.elementary-partner-finder-2025__no-results';
	const anyResultsSelector = `${resultsSelector}, ${noResultsSelector}`;
	const clearFiltersSelector = '[data-action="clear-filters"]';
	const productInputSelector = 'input[type="checkbox"][name="pids[]"]';
	const activeFiltersSelector = '.elementary-partner-finder-2025__active-filters';
	const removeFilterButtonSelector = '[data-action="remove-product-filter"]';
	const debugSelector = '.partner-finder-debug';
	const debugBodyRowSelector = '.partner-finder-debug table tbody tr';

	const debugContainerEl = document.querySelector(debugSelector);

	/****/
	class PartnerFinder {
		// Elements
		#rootEl;
		#activeAbortController;

		// Vars
		#previousFormData;

		/****/
		constructor(rootEl) {
			this.#rootEl = rootEl;

			this.#previousFormData = new FormData();

			this.#rootEl.addEventListener('change', this.#onChange.bind(this), { capture: true});
			this.#rootEl.addEventListener('keyup', this.#onKeyUp.bind(this), { capture: true});
			this.#rootEl.addEventListener('submit', this.#onSubmit.bind(this));

			this.#rootEl.addEventListener('click', this.#removeFilterClicked.bind(this));
			this.#rootEl.addEventListener('click', this.#clearAllClicked.bind(this));
		}

		/****/
		#removeFilterClicked(event) {
			//
			const targetEl = event.target;
			if (!(targetEl instanceof HTMLElement)) return false;
			if (!matchesSelector(event.target, removeFilterButtonSelector)) return;
			event.preventDefault();

			// Find the remove button
			const buttonEl = targetEl.closest(removeFilterButtonSelector);
			if (!(buttonEl instanceof HTMLElement)) return false;

			// Get all filters
			const allFilterEls = [...this.#rootEl.querySelectorAll(productInputSelector)];
			if (0 === allFilterEls.length) return;

			// Find the filter input to toggle
			const filterId = buttonEl.dataset.id;
			const filterEl = allFilterEls.find(el => el.getAttribute('value') === filterId);
			if (!(filterEl instanceof HTMLElement)) return false;

			// Toggle the filter input
			filterEl.checked = false;
			const changeEvent = new Event('change');
			filterEl.dispatchEvent(changeEvent);
		}

		/****/
		#clearAllClicked(event) {
			const targetEl = event.target;
			if (!(targetEl instanceof HTMLElement)) return false;
			if (!matchesSelector(event.target, clearFiltersSelector)) return;
			event.preventDefault();

			const buttonEl = targetEl.closest(clearFiltersSelector);
			const allFilterEls = [...this.#rootEl.querySelectorAll(productInputSelector)];

			if (0 < allFilterEls.length) {
				allFilterEls.forEach(o => o.checked = false);
				const changeEvent = new Event('change');
				allFilterEls[0].dispatchEvent(changeEvent);
			}
		}

		/****/
		#onKeyUp(event) {
			const targetEl = event.target;
			if (!(targetEl instanceof HTMLElement)) return false;
			if (!matchesSelector(event.target, inputSelector)) return;

			const inputEl = targetEl.closest(inputSelector);
			const formEl = inputEl.closest('form');
			this.#doSubmit(formEl);
		}

		/****/
		#onChange(event) {
			const formEl = event.target.closest('form');
			this.#doSubmit(formEl);
		}

		/****/
		#onSubmit(event) {
			event.preventDefault();
			event.stopPropagation();

			const formEl = event.target.closest('form');
			this.#doSubmit(formEl);
		}

		/****/
		#doSubmit(formEl) {
			//
			const formAction = formEl.getAttribute('action');
			const formData  = new FormData(formEl);
			const urlParams = new URLSearchParams(formData);
			const url	   = new URL(formAction);
			url.search	  = urlParams.toString();

			//
			if (!this.#hasFormDataChanged(formData, this.#previousFormData)) return;

			//
			if (this.#activeAbortController instanceof AbortController) {
				this.#activeAbortController.abort();
			}

			//
			this.#activeAbortController = new AbortController();
			const fetchOptions	= {
				signal: this.#activeAbortController.signal,
			};

			//
			window.history.replaceState(null, null, url.toString());

			//
			fetch(url.toString(), fetchOptions)
				.then(o => o.text())
				.then(this.#updatePage.bind(this))
				.finally(() => this.#activeAbortController = undefined);

			//
			this.#previousFormData = formData;
		}

		/****/
		#updatePage(markup) {
			//
			const parser	  = new DOMParser();
			const newDocument = parser.parseFromString(markup, 'text/html');

			//
			const newRootEl = newDocument.getElementById(this.#rootEl.id);
			if (!(newRootEl instanceof HTMLElement)) return;

			// Replace the results
			const newResultsEl = newRootEl.querySelector(anyResultsSelector);
			const oldResultsEl = this.#rootEl.querySelector(anyResultsSelector);
			if (
				newResultsEl instanceof HTMLElement &&
				oldResultsEl instanceof HTMLElement
			) {
				oldResultsEl.replaceWith(newResultsEl);
			}

			// Replace the active filters
			const newFiltersEl = newRootEl.querySelector(activeFiltersSelector);
			const oldFiltersEl = this.#rootEl.querySelector(activeFiltersSelector);
			if (
				newFiltersEl instanceof HTMLElement &&
				oldFiltersEl instanceof HTMLElement
			) {
				oldFiltersEl.replaceWith(newFiltersEl);
			}

			// Replace debug
			const newDebugEl = newDocument.querySelector(debugSelector);
			const oldDebugEl = debugContainerEl;
			if (
				newDebugEl instanceof HTMLElement &&
				oldDebugEl instanceof HTMLElement
			) {
				oldDebugEl.innerHTML = newDebugEl.innerHTML;
			}
		}

		/****/
		#hasFormDataChanged(currentData, previousData) {
			const currentProductIds = currentData.getAll('pids[]') ?? [];
			const previousProductIds = previousData.getAll('pids[]') ?? [];
			const changedProductIds = currentProductIds.filter(x => !previousProductIds.includes(x))
				.concat(previousProductIds.filter(x => !currentProductIds.includes(x)));

			const productIdsChanged = 0 < changedProductIds.length;

			return productIdsChanged;
		}
	}

	/****/
	function populateDebugCalculator(event) {
		//
		const targetEl = event.target;
		if (!(targetEl instanceof HTMLElement)) return;
		if (!matchesSelector(targetEl, debugBodyRowSelector)) return;

		//
		const dataRowEl = targetEl.closest(debugBodyRowSelector);
		const dataEls   = [...dataRowEl.querySelectorAll('[data-input]')];

		//
		dataEls.forEach(dataEl => {
			const inputName = String(dataEl.dataset.input);
			const value = dataEl.dataset.value ?? dataEl.innerText;

			const inputEl = debugContainerEl.querySelector(`#${inputName}`);
			if (!(inputEl instanceof HTMLElement)) return;

			inputEl.value = value;

			const changeEvent = new Event('change', { bubbles: true });
			inputEl.dispatchEvent(changeEvent);
		});
	}

	/****/
	function updateDebugCalculator(event) {

		//
		const partnerScoreEl = debugContainerEl.querySelector('#debug-partner-score');
		if (!(partnerScoreEl instanceof HTMLElement)) return;

		//
		event.preventDefault();

		//
		const manualWeight = parseInt(debugContainerEl.querySelector('#debug-manual-weight').value ?? '0', 0);
		const referenceProjects = parseInt(debugContainerEl.querySelector('#debug-reference-projects').value ?? '0', 0);
		const referenceProjectsModifier = parseFloat(debugContainerEl.querySelector('#debug-reference-projects-modifier').value ?? '0');
		const directPartnerNum = parseInt(debugContainerEl.querySelector('#debug-direct-partner-num').value ?? '0', 0);
		const directPartnerModifier = parseFloat(debugContainerEl.querySelector('#debug-direct-partner-modifier').value ?? '0');
		const schucoMentionNum = parseInt(debugContainerEl.querySelector('#debug-schuco-mention-num').value ?? '0', 0);
		const schucoMentionModifier = parseFloat(debugContainerEl.querySelector('#debug-schuco-mention-modifier').value ?? '0');
		const yearsWith = parseInt(debugContainerEl.querySelector('#debug-years-with').value ?? '0', 0);
		const yearsWithModifier = parseFloat(debugContainerEl.querySelector('#debug-years-with-modifier').value ?? '0');
		const showroomProductsPerc = parseInt(debugContainerEl.querySelector('#debug-showroom-products-perc').value ?? '0', 0);
		const showroomProductsPercModifier = parseFloat(debugContainerEl.querySelector('#debug-showroom-products-perc-modifier').value ?? '0');
		const showroomQuality = parseInt(debugContainerEl.querySelector('#debug-showroom-quality').value ?? '0', 0);
		const showroomQualityModifier = parseFloat(debugContainerEl.querySelector('#debug-showroom-quality-modifier').value ?? '0');
		const shareOfWallet = parseInt(debugContainerEl.querySelector('#debug-share-of-wallet').value ?? '0', 0);
		const shareOfWalletModifier = parseFloat(debugContainerEl.querySelector('#debug-share-of-wallet-modifier').value ?? '0');
		const noOfProducts = parseInt(debugContainerEl.querySelector('#debug-no-of-products').value ?? '0', 0);
		const noOfProductsModifier = parseFloat(debugContainerEl.querySelector('#debug-no-of-products-modifier').value ?? '0');

		//
		const finalScore = (
			manualWeight +
			(referenceProjects * referenceProjectsModifier) +
			(directPartnerNum * directPartnerModifier) +
			(schucoMentionNum * schucoMentionModifier) +
			(yearsWith * yearsWithModifier) +
			(showroomProductsPerc * showroomProductsPercModifier) +
			(showroomQuality * showroomQualityModifier) +
			(shareOfWallet * shareOfWalletModifier) +
			(
				(noOfProducts * noOfProductsModifier) / 8
			)
		);

		//
		partnerScoreEl.value = finalScore.toFixed(2);
	}

	//
	const rootEls = [...document.querySelectorAll(rootSelector)];
	rootEls.forEach(o => new PartnerFinder(o));

	//
	if (debugContainerEl instanceof HTMLElement) {
		debugContainerEl.addEventListener('click', populateDebugCalculator);
		debugContainerEl.addEventListener('change', updateDebugCalculator);
	}
});
